import React, { useCallback, useMemo } from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import DropDown from '@components/dropdown/v2'

import { Level } from '@api/building'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'

import { Unit } from '@adUtilities/types/apartment'

interface DropdownProps {
  projectIdentity: ProjectIdentity
  activeBlock: string
  blocks: string[]
  levels: Array<Level>
  showAvailable: boolean
  availableLabel: string
}
function BlockDropdown({
  projectIdentity,
  activeBlock,
  blocks,
  levels,
  showAvailable,
  availableLabel,
}: DropdownProps) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const blockOptions = useMemo(
    () =>
      blocks.map((label) => ({
        label,
        value: label,
      })),
    [blocks]
  )

  const getFirstLevel = useCallback(
    (selectedBlock: string) => {
      const filteredLevels = levels.filter((lvl) =>
        showAvailable
          ? lvl.data.some((unit: Unit) => unit.metas.status === availableLabel)
          : true
      )

      const firstLevel = filteredLevels.find((lvl) =>
        lvl.data.some((unit) => unit.blockId === selectedBlock)
      )

      return firstLevel?.level || filteredLevels[0]?.level
    },
    [levels, availableLabel, showAvailable]
  )

  const onChange = useCallback(
    async (value: string) => {
      const firstLevel = getFirstLevel(value)
      await firebaseControlQuery.update({
        [`building.activeBlock`]: value,
        [`building.activeLevel`]: firstLevel,
        [`building.activeUnit`]: '',
      })
    },
    [firebaseControlQuery]
  )

  return (
    <DropDown
      onSelect={onChange}
      items={blockOptions}
      value={activeBlock || blockOptions[0].value}
      className="py-2"
      buttonRadius="rounded-[7px]"
      buttonRadiusClasses="rounded-tl-[7px] rounded-tr-[7px]"
      optionRadiusClasses="rounded-bl-[7px] rounded-br-[7px]"
    />
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    activeBlock: session?.building?.activeBlock || '',
    showAvailable: session?.building?.unitFilter?.showAvailable || false,
    availableLabel: projectIdentity?.statusLabels?.available || 'available',
  })
)(BlockDropdown)
